import {inject, Injectable} from '@angular/core';
import {SessionService} from "./session.service";
import {StorageService} from "./storage.service";

@Injectable({
  providedIn: 'root'
})
export class UserStorageService {
  private currentUserId: string | null = null;
  private readonly sessionService = inject(SessionService);
  private readonly storageService = inject(StorageService);

  readonly serviceRequestKey = 'serviceRequest';

  constructor() {
    this.sessionService.currentUser.subscribe((res: any) => {
      this.currentUserId = res?.user?.user_id ?? null;
    });
  }

  setValue(key: string, value: string) {
    const storageKey = this.getStorageKey(key);
    this.storageService.setValue(storageKey, value);
  }

  getValue(key: string) {
    const storageKey = this.getStorageKey(key);
    return this.storageService.getValue(storageKey);
  }

  removeValue(key: string) {
    const storageKey = this.getStorageKey(key);
    return this.storageService.removeValue(storageKey);
  }

  removeFromLocalStorageArray(key: string, objectToRemove: { asset_id: any }) {
    let currentArray: any[] = [];
    try {
      currentArray = JSON.parse(this.getValue(key) || '[]');
    } catch (error) {
      console.error('Failed to parse storage array:', error);
      return;
    }

    const index = currentArray.findIndex((obj: { asset_id: any }) =>
      obj.asset_id == objectToRemove.asset_id);
    if (index !== -1) {
      currentArray.splice(index, 1);
      if (currentArray.length === 0) {
        this.removeValue(key);
      } else {
        this.setValue(key, JSON.stringify(currentArray));
      }
    }
  }

  private getItemLocation(item: any): any {
    return item.location ?? item.locationName ?? item.LocationName ?? item.location_name;
  }

  addToStorageServiceRequest(item: any): { message: string, type: 'success' | 'error' | 'info' } {
    const currentItemsString = this.getValue(this.serviceRequestKey);
    let currentItems: any[] = [];
    if (currentItemsString) {
      try {
        currentItems = JSON.parse(currentItemsString);
      } catch (error) {
        console.error('Failed to parse service request items:', error);
      }
    }
    const existItem = currentItems.findIndex(e => e.asset_id === item.asset_id);
    const itemLocation = this.getItemLocation(item);

    // Check if all items have the same location as the new item
    const locationMismatch = currentItems.length > 0 && currentItems.some(e => e.location !== itemLocation);

    if (locationMismatch) {
      return {message: 'You can\'t add asset from another location to Service Request', type: 'error'};
    }
    if (existItem === -1) {
      currentItems.push({
        origin: item.origin,
        asset_id: item.asset_id,
        asset_number: item.asset_number,
        location: itemLocation,
        serial_number: item.serial_number,
        model: item.asset_model,
        make: item.asset_make,
        type: item.asset_type,
        is_active: item.is_active,
        is_sn_unknown: item.is_sn_unknown,
        make_id: item.make_id,
        type_id: item.type_id,
        model_id: item.model_id,
        service_request_asset_number: currentItems.length
      });

      this.setValue(this.serviceRequestKey, JSON.stringify(currentItems));
      return {message: 'Asset successfully added to Service Request', type: 'success'};
    } else {
      return {message: 'You already added this asset to Service Request', type: 'error'};
    }
  }

  /**
   * Generate storage key
   * @param key - Unique identifier of a state object. Use kebab case for naming
   * @returns Storage key in format: [userId]_[key]
   */
  private getStorageKey(key: string): string {
    return `${this.currentUserId}_${key}`;
  }
}
